import { MouseEvent, useState } from 'react';

import { copyTreeToNodes } from '../../utils/copy-tree-to-nodes';
import { shallowUniqueFilter } from '../../utils/shallow-unique-filter';
import { InteractableMap } from './use-interactable-map';
import { ContextMenu } from './use-mindmap-context-menu';
import { Data } from './use-mindmap-data';
import { Positioning } from './use-mindmap-positioning';

export interface Selection {
  selectedNodeIds: string[];
  setSelectedNodeIds: React.Dispatch<React.SetStateAction<string[]>>;
  unselectNodes: () => void;
  onMapClick: (e: MouseEvent) => void;
  toggleNodeSelection: (id: string, { append }: { append: boolean }) => void;
  isNodeSelected: (id: string) => boolean;
  onDrawingSelection: (
    topLeftCords: { x: number; y: number },
    bottomRightCords: { x: number; y: number },
    append: boolean,
  ) => void;
  isEditingNode: boolean;
  setIsEditingNode: React.Dispatch<React.SetStateAction<boolean>>;
  isDrawingSelection: boolean;
  setIsDrawingSelection: React.Dispatch<React.SetStateAction<boolean>>;
  updateSelectedNodeIds: (nodeIds: string[], append: boolean) => void;
  selectAllNodes: () => void;
}

type UseMindmapSelection = {
  positioning: Positioning;
  data: Data;
  interactableMap: InteractableMap;
  contextMenu: ContextMenu;
};

export const useMindmapSelection = ({
  positioning: { positionedTree },
  data: { nodeMap },
  contextMenu: { hideContextMenu },
}: UseMindmapSelection): Selection => {
  const [selectedNodeIds, setSelectedNodeIds] = useState<string[]>([]);
  const [isEditingNode, setIsEditingNode] = useState<boolean>(false);
  const [isDrawingSelection, setIsDrawingSelection] = useState<boolean>(false);

  const isNodeSelected = (id: string) => selectedNodeIds.includes(id);

  const unselectNodes = () => setSelectedNodeIds([]);

  const toggleNodeSelection = (id: string, { append }: { append: boolean }) => {
    if (isNodeSelected(id)) {
      setSelectedNodeIds(!append ? [] : selectedNodeIds.filter((_id) => _id !== id));
    } else {
      setSelectedNodeIds(append ? [...selectedNodeIds, id] : [id]);
    }
  };

  const updateSelectedNodeIds = (nodeIds: string[], append: boolean = false) => {
    setSelectedNodeIds(append ? [...selectedNodeIds, ...nodeIds].filter(shallowUniqueFilter) : nodeIds);
  };

  const selectAllNodes = () => {
    setSelectedNodeIds(Object.values(nodeMap).map(({ id }) => id));
  };

  const onMapClick = (e: MouseEvent) => {
    // @ts-ignore make clicks on context menu not count as map clicks
    const clickedOnContextMenu = e.nativeEvent?.path?.find(({ id }) => id == 'tool-panel-card');
    if (clickedOnContextMenu) return;

    // unselect all nodes if not in the "append"-mode
    if (!e.ctrlKey) unselectNodes();
    setIsEditingNode(false);
    hideContextMenu();
  };

  const onDrawingSelection = (
    topLeftCords: { x: number; y: number },
    bottomRightCords: { x: number; y: number },
    append: boolean,
  ) => {
    if (topLeftCords.x == bottomRightCords.x && topLeftCords.y == bottomRightCords.y) {
      // drawn area size is 0
      return;
    }

    if (positionedTree) {
      // get an array with all nodes and their positions on the map
      const flatPositionedNodes = copyTreeToNodes([positionedTree], {}, { keepIds: true });

      // find nodes that are under the drawed area
      const nodeIdsUnderArea: string[] = [];

      flatPositionedNodes.forEach((node) => {
        const { x, y, width, height } = node;
        if (x !== undefined && y !== undefined) {
          if (
            (x >= topLeftCords.x || x + width >= topLeftCords.x) &&
            (x <= bottomRightCords.x || x + width <= bottomRightCords.x) &&
            (y >= topLeftCords.y || y + height >= topLeftCords.y) &&
            (y <= bottomRightCords.y || y + height <= bottomRightCords.y)
          ) {
            nodeIdsUnderArea.push(node.id);
          }
        }
      });

      updateSelectedNodeIds(nodeIdsUnderArea, append);
    }
  };

  return {
    selectedNodeIds,
    onDrawingSelection,
    setSelectedNodeIds,
    unselectNodes,
    onMapClick,
    toggleNodeSelection,
    isNodeSelected,
    isEditingNode,
    setIsEditingNode,
    isDrawingSelection,
    setIsDrawingSelection,
    updateSelectedNodeIds,
    selectAllNodes,
  };
};
